import { ProcessedMessage } from './useProcessMessage'
import useUser from '../useUser'
import { useHistory, useLocation } from 'react-router-dom'
import { Command, IBackgroundLocationState } from '../../config/appConfig'
import { COMMAND_PREFIX } from '../../pages/Commands'
import { TimeTrackingNotification } from './useProcessTimeTrackingNotification'
import useAppToast from '../useAppToast'
import { useQueue } from '../useQueue'
import { isTimeSuggestionJob } from '../queue/queueTypes'
import { useContext } from 'react'
import PopupContext from '../../context/PopupContext'
import { ModuleType } from '../../context/GlobalContext'
import useRoles, { rolesWriteAllTimes } from '../useRoles'
import useModules from '../module/useModules'

/**
 * Message in order to revoke a TimeSuggestionDialog.
 */
export type CancelTimeTrackingNotification = ProcessedMessage<{
    timeSuggestionId: string
}>

/**
 * Process a cancel time tracking event message. This revokes the TimeSuggestionDialog.
 */
export default function useProcessCancelTimeTrackingNotification() {
    const history = useHistory<IBackgroundLocationState>()
    const { currentUser } = useUser()
    const location = useLocation<TimeTrackingNotification>()
    const modules = useModules()
    const popupContext = useContext(PopupContext)
    const roles = useRoles()
    const toast = useAppToast()
    const queue = useQueue()

    return (message: CancelTimeTrackingNotification) => {
        if (!modules.has(ModuleType.TimeTracking) || !roles.any(rolesWriteAllTimes)) {
            return
        }

        if (message.data?.timeSuggestionId === undefined) {
            return
        }

        const timeSuggestionId = message.data.timeSuggestionId

        // Matched rather than looked up by id: the counter raises its toasts under an id with the round appended,
        // so a plain dismiss missed exactly those and they stayed on screen in the other window. Clearing up runs
        // for everyone, including a user who has the popups switched off - the counter shows them toasts anyway.
        toast.dismissWhere(toastId => isTimeSuggestionJob(String(toastId), timeSuggestionId))
        // Also drop the job, so the same suggestion can be offered again instead of being rejected as a duplicate.
        queue.remove(timeSuggestionId)
        popupContext.cancelTimeSuggestionDialog(timeSuggestionId)

        if (currentUser?.useTimePopup === false) {
            // Only navigate away from the time tracking popup when the user actually uses the popups.
            return
        }

        //Only cancel the time tracking dialog when the timeSuggestionId match.
        if (
            location.pathname === `${COMMAND_PREFIX}/${Command.TrackTime}` &&
            message.data?.timeSuggestionId === location.state?.data?.timeSuggestionId
        ) {
            history.push(history.location.state?.background?.pathname || '/')
        }
    }
}
